import React, { useState, useEffect } from "react"; // Add useEffect import
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";
import styles from "./ReserveForm.module.css";
import SelectInput from "../common/select-input/select-input";
import InputText from "../common/input-text/input-text";
import ButtonSubmit from "../common/button-submit/button-submit";

type FormData = {
  from_name: string;
  email: string;
  phone: string;
  subject: string;
  location: string;
  message: string;
  number_of_people: { value: string; label: string } | null;
  date: string;
  time: string;
};

const ReserveForm: React.FC = () => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>();

  const numberOfPeopleOptions = Array.from({ length: 8 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `${i + 1} Pess.`,
  }));

  // Initialize selectedNumberOfPeople with the option for 2 people
  const [selectedNumberOfPeople, setSelectedNumberOfPeople] = useState<{
    value: string;
    label: string;
  } | null>(numberOfPeopleOptions[1]); // Index 1 corresponds to "2 Pess."

  // Set default value for number_of_people in the form
  useEffect(() => {
    setValue("number_of_people", numberOfPeopleOptions[1]);
  }, [setValue, numberOfPeopleOptions]); // Run once on component mount

  const sendEmail: SubmitHandler<FormData> = (data) => {
    const dataWithPeople = {
      ...data,
      number_of_people: selectedNumberOfPeople
        ? selectedNumberOfPeople.value
        : "",
    };

    emailjs
      .send(
        "service_wizymj4",
        "template_gutxvhp",
        dataWithPeople,
        "kA0TDkpXEVT1HlrvD"
      )
      .then(
        () => {
          console.log("Email sent successfully!");
          reset();
          setSelectedNumberOfPeople(null);
        },
        (error) => {
          console.log("Failed to send email...", error);
        }
      );
  };

  return (
    <section className={styles.reserveFormSection}>
      <h2 className={styles.reserveFormSectionTitle}>{t("O Xiang")}</h2>
      <div className={styles.filterBox}>
        <div className={styles.filterItem}>
          <SelectInput
            name="number_of_people"
            value={selectedNumberOfPeople}
            visibleOptions={numberOfPeopleOptions}
            onValueChange={(option) => {
              setSelectedNumberOfPeople(option);
              setValue("number_of_people", option);
            }}
            translate={t}
          />
        </div>
        <div className={styles.filterItem}>
          <InputText
            type="date"
            name="date"
            value={watch("date") || new Date().toISOString().split("T")[0]}
            onChange={(value) => setValue("date", value)}
          />
        </div>
        <div className={styles.filterItem}>
          <InputText
            type="time"
            name="time"
            value={watch("time") || "21:00"}
            onChange={(value) => setValue("time", value)}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(sendEmail)} className={styles.reserveForm}>
        <div className={styles.formInputsGrid}>
          <InputText
            name="from_name"
            label={t("reserve_form.fields.name")}
            placeholder={t("reserve_form.fields.name")}
            required
            value={watch("from_name") || ""}
            onChange={(value) => setValue("from_name", value)}
            validateField={(value) => errors.from_name?.message}
            translate={t}
          />

          <InputText
            name="email"
            label={t("reserve_form.fields.email")}
            placeholder={t("reserve_form.fields.email")}
            required
            type="email"
            value={watch("email") || ""}
            onChange={(value) => setValue("email", value)}
            validateField={(value) => errors.email?.message}
            translate={t}
          />

          <InputText
            name="phone"
            label={t("reserve_form.fields.phone")}
            placeholder={t("reserve_form.fields.phone")}
            required
            type="tel"
            value={watch("phone") || ""}
            onChange={(value) => setValue("phone", value)}
            validateField={(value) => errors.phone?.message}
            translate={t}
          />
        </div>

        <div className={styles.reserveFormButton}>
          <ButtonSubmit
            buttonType="submit"
            text={t("reserve_form.submit_button")}
            onClick={() => {}}
            translate={t}
          />
        </div>
      </form>
    </section>
  );
};

export default ReserveForm;
